import { TicketProps } from 'app/components/TicketCarousel/Ticket';
import { Locale } from 'app/generated/hygraph';
import { AttractionContent } from 'app/hooks/useAttraction';
import { ProductContent } from 'app/hooks/useProduct';
import {
  EntitlementReservation,
  Pass,
  TicketCategory,
  TicketClass,
} from 'app/services/GuestCenterService.types';
import { ReservationTextHelper } from 'app/services/I18n';
import { getOptionContent } from 'app/services/ReservationHelper';

export function createTicket({
  pass,
  productContent: product,
  attractionKey,
  orderNumber,
  ticketCategory,
}: {
  pass: Pass;
  productContent: ProductContent;
  attractionKey: string;
  orderNumber?: string;
  ticketCategory?: TicketCategory;
}): TicketProps {
  const ticket = pass.tickets.find(
    ($ticket) =>
      $ticket.attractionKey === attractionKey &&
      $ticket.barcode &&
      (ticketCategory ? $ticket.categoryKey === ticketCategory.categoryKey : true)
  );

  const productCodeTicketNames =
    product.ticketGroups?.reduce<Record<string, string>>(
      (acc, { key: groupKey, title, subtitle }) => {
        if (groupKey) {
          if (title && subtitle) {
            acc[groupKey] = `${title} ${subtitle}`;
          } else {
            acc[groupKey] = title;
          }
        }
        return acc;
      },
      {}
    ) || {};

  return {
    logo: {
      url: product.logo?.url ?? '',
      width: product.logo?.width ?? 1,
      height: product.logo?.height ?? 1,
    },
    ticketName: getTicketName(
      product.cityName?.value || product.name,
      pass.productCode,
      productCodeTicketNames
    ),
    barcodeScan: ticket?.scan ?? ticket?.barcode ?? pass.scan ?? pass.barcode,
    barcodeDisplay: ticket?.display ?? ticket?.barcode ?? pass.display ?? pass.barcode,
    guestName: pass.name,
    ageRange:
      (pass.ticketClass === TicketClass.Adult ? product.ageAdult : product.ageChild) ?? null,
    ticketClass: pass.ticketClass,
    orderNumber,
    ticketCategory,
  };
}

export function createReservationTicket({
  attractionContent,
  reservation,
  rt,
  ticketCategory,
}: {
  attractionContent: AttractionContent;
  reservation: EntitlementReservation;
  rt: ReservationTextHelper;
  ticketCategory?: TicketCategory;
}) {
  return {
    reservation,
    attractionName: attractionContent.name ?? '',
    dateTime: reservation.reservationDateTime,
    option: getOptionContent(reservation, { type: 'title', rt, ticketCategory, locale: Locale.En }),
    referenceNumber: reservation.display,
    type: ticketCategory?.reservationType ?? reservation.type,
    ticketCategory,
  };
}

function getTicketName(
  cityName?: Maybe<string>,
  productCode?: Maybe<string>,
  productCodeTicketNames?: Record<string, string>
) {
  const productCodeTicketName = productCode && productCodeTicketNames?.[productCode];
  return productCodeTicketName ?? `${cityName ?? ''} ${productCode ?? 'CityPASS'}`;
}
